import {
  PRIORITY_FACTOR_HELP,
  PRIORITY_TIER_LABELS,
  type PriorityFactorDto,
  type PriorityTier,
} from '@nova/shared';
import { cn } from '../../lib/cn.js';

const TIER_TONE: Record<PriorityTier, string> = {
  A: 'bg-success/15 text-success border-success/30',
  B: 'bg-warning/12 text-warning border-warning/25',
  C: 'bg-ink-soft/10 text-ink-soft border-ink-soft/20',
};

/** The effective tier chip. A hand icon marks a manual override. */
export function TierChip({
  tier,
  overridden = false,
  size = 'md',
}: {
  tier: PriorityTier;
  overridden?: boolean;
  size?: 'sm' | 'md';
}) {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 rounded-full border font-semibold',
        size === 'sm' ? 'px-1.5 py-0.5 text-[10px]' : 'px-2 py-0.5 text-xs',
        TIER_TONE[tier],
      )}
      title={overridden ? 'Manuelt overstyrt tier' : 'Prioritetstier'}
    >
      {PRIORITY_TIER_LABELS[tier]}
      {overridden && <span aria-hidden>✎</span>}
    </span>
  );
}

/** A compact 0–100 score meter. */
export function ScoreBar({ score, className }: { score: number; className?: string }) {
  const tone = score >= 70 ? 'bg-success' : score >= 45 ? 'bg-warning' : 'bg-ink-soft';
  return (
    <div className={cn('flex items-center gap-2', className)}>
      <div className="bg-base/60 relative h-1.5 w-20 overflow-hidden rounded-full">
        <div className={cn('absolute inset-y-0 left-0 rounded-full', tone)} style={{ width: `${score}%` }} />
      </div>
      <span className="text-ink tabular-nums text-sm font-medium">{score}</span>
    </div>
  );
}

/**
 * The factor breakdown — every factor's sub-score, weight and the points it
 * contributed, with its Norwegian detail. Nothing about the score is hidden.
 */
export function FactorBreakdown({ factors }: { factors: PriorityFactorDto[] }) {
  const maxContribution = Math.max(1, ...factors.map((f) => f.contribution));
  return (
    <ul className="space-y-2">
      {factors.map((f) => (
        <li key={f.key}>
          <div className="flex items-center justify-between gap-2 text-xs">
            <span className="text-ink font-medium" title={PRIORITY_FACTOR_HELP[f.key]}>
              {f.label}
            </span>
            <span className="text-ink-soft tabular-nums">
              {f.raw}/100 · vekt {f.weight} · +{f.contribution.toFixed(1)}p
            </span>
          </div>
          <div className="bg-base/60 mt-1 h-1 w-full overflow-hidden rounded-full">
            <div
              className="bg-accent/70 h-full rounded-full"
              style={{ width: `${(f.contribution / maxContribution) * 100}%` }}
            />
          </div>
          <p className="text-ink-soft/80 mt-0.5 text-[11px] leading-relaxed">{f.detail}</p>
        </li>
      ))}
    </ul>
  );
}
